// QBR — adoption fetcher.
//
// Pulls /v1/users for a project and computes the share of eligible users
// who actually logged in within the last 30 and 90 days from periodEnd.
//
// "Eligible" = users who can authenticate and aren't deleted, contacts,
// or integration service accounts. Their status must also not be a known
// "off" state. Counting those would understate adoption.
//
// Denominator note (deliberate product choice): "Unregistered" and
// "Email disabled" users stay eligible even though they've likely never
// logged in — they're staff who *should* be adopting, so they drag the
// active-% down by design. UserReadModel `status` for a normal active
// user is null.
//
// Mobile usage % isn't exposed on the user record, so it stays a manual
// input on the data step.

// Shares the QBR concurrency gate with fmxData.js so adoption's /v1/users
// paging counts against the same global in-flight ceiling. Aliased to fmxFetch
// so the call site below reads unchanged.
import { qbrFmxFetch as fmxFetch, fmxErrorText } from './fmxThrottle.js';

const PAGE_LIMIT = 500; // FMX enforces no limit clamp; large pages cut round trips
const MAX_PAGES  = 50;  // safety cap → up to 25,000 users

// Only the fields the eligibility filter + adoption math + Top Requestors
// hydration consume — without fields= each page carries full user payloads
// (custom fields, equipment assignments, open-work ID arrays, …).
const USER_FIELDS = [
  'id', 'name', 'email', 'status', 'isContact', 'isDeleted',
  'canAuthenticate', 'isUsedByIntegration', 'lastLoginTimeUtc',
  'userTypeID', 'accessibleBuildingIDs',
].join(',');

// Known terminal/off states for a user record. Matched as exact strings
// (case-insensitive) — not as a substring — because FMX uses compound
// status values like "Email disabled" that contain "disabled" but
// describe a user who can still log in. Per UserModelMapper the produced
// values are "Deleted" / "Disabled" / "Unregistered" / "Email disabled" /
// null; the extra entries here are harmless tenant-drift insurance.
const INELIGIBLE_STATUSES = new Set([
  'disabled', 'retired', 'inactive', 'deleted', 'suspended',
]);
function isIneligibleStatus(status) {
  return typeof status === 'string'
    && INELIGIBLE_STATUSES.has(status.trim().toLowerCase());
}

export async function fetchAdoptionData({ projectId, periodEnd, powerUserTypeIds = null }) {
  const { users, truncationWarning } = await paginateUsers({ projectId });
  const overall = computeAdoption(users, periodEnd);
  if (truncationWarning) overall.truncationWarning = truncationWarning;

  // Power-user variant — same eligibility filter as `overall`, then
  // further narrowed to users whose userTypeID is in the FMXer's
  // selection. Skipped when no selection was provided (Setup step
  // hasn't shipped the choice, or there are no user-types loaded).
  let powerUser = null;
  if (Array.isArray(powerUserTypeIds) && powerUserTypeIds.length > 0) {
    const idSet = new Set(powerUserTypeIds.map(Number));
    const filtered = users.filter(u => idSet.has(Number(u?.userTypeID)));
    powerUser = computeAdoption(filtered, periodEnd);
  }

  return { ...overall, powerUser };
}

// Pure function — fetch separated from compute so the math is testable
// without a live FMX call. See scripts/test-adoption.mjs.
//
// Window note: lastLoginTimeUtc is a single overwritten snapshot (the
// API has no login history), so the cutoffs are deliberately NOT
// upper-bounded at periodEnd. Bounding would DROP every user who was
// active in-window and has logged in since (their snapshot moved past
// the window) — far worse than the small overcount from users whose
// only login came shortly after periodEnd. Consequence: the number is
// only trustworthy when the QBR runs soon after the quarter ends, and
// a historical quarter can't be recomputed later — which is also why
// fetchPriorQuarterSummary doesn't attempt a prior-quarter adoption.
export function computeAdoption(users, periodEnd) {
  const endMs = Date.parse(periodEnd) + 24 * 3600 * 1000 - 1;
  const day = 24 * 3600 * 1000;
  const cutoff30 = endMs - 30 * day;
  const cutoff90 = endMs - 90 * day;

  const exclusions = {
    deleted: 0, contact: 0, cannotAuth: 0, integration: 0, statusOff: 0,
  };
  const statusCounts = {};
  let eligibleCount = 0;
  let active30 = 0;
  let active90 = 0;
  let withLoginRecord = 0;
  let mostRecentLoginMs = null;

  for (const u of users) {
    const statusKey = u?.status ?? '(none)';
    statusCounts[statusKey] = (statusCounts[statusKey] || 0) + 1;

    const reason = excludeReason(u);
    if (reason) {
      exclusions[reason] += 1;
      continue;
    }
    eligibleCount += 1;

    const lastMs = u.lastLoginTimeUtc ? Date.parse(u.lastLoginTimeUtc) : NaN;
    if (Number.isNaN(lastMs)) continue;
    withLoginRecord += 1;
    if (mostRecentLoginMs == null || lastMs > mostRecentLoginMs) mostRecentLoginMs = lastMs;
    if (lastMs >= cutoff30) active30 += 1;
    if (lastMs >= cutoff90) active90 += 1;
  }

  const sample = users[0];
  const userSampleKeys = sample ? Object.keys(sample).sort() : [];
  const mostRecentLogin = mostRecentLoginMs != null
    ? new Date(mostRecentLoginMs).toISOString().slice(0, 10)
    : null;

  return {
    endpointUsed: '/v1/users',
    counts: {
      totalPulled: users.length,
      eligible: eligibleCount,
      active30,
      active90,
      withLoginRecord,
    },
    exclusions,
    statusCounts,
    mostRecentLogin,
    userSampleKeys,
    activePct30: eligibleCount > 0 ? round1((active30 / eligibleCount) * 100) : null,
    activePct90: eligibleCount > 0 ? round1((active90 / eligibleCount) * 100) : null,
    // Raw user list — exposed so the deck builder can aggregate by
    // requestingUserID for the 'Top Requestors' slide without
    // re-pulling /v1/users. Filtered to eligible only since that's the
    // population we ever care about.
    eligibleUsers: users.filter(u => excludeReason(u) == null),
  };
}

// Returns the exclusion bucket name, or null if the user is eligible.
// Pure function — exported for tests.
export function excludeReason(u) {
  if (!u) return 'deleted';
  if (u.isDeleted) return 'deleted';
  if (u.isContact) return 'contact';
  if (u.canAuthenticate === false) return 'cannotAuth';
  if (u.isUsedByIntegration) return 'integration';
  if (isIneligibleStatus(u.status)) return 'statusOff';
  return null;
}

async function paginateUsers({ projectId }) {
  const out = [];
  let headerTotal = null;
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = `/v1/users?offset=${offset}&limit=${PAGE_LIMIT}&fields=${encodeURIComponent(USER_FIELDS)}`;
    const res = await fmxFetch({ projectId, endpoint: url, method: 'GET' });
    if (!res.ok) throw new Error(await fmxErrorText(res, '/v1/users'));
    if (page === 0) {
      const t = parseInt(res.headers.get('FMX-Total-Count'), 10);
      if (Number.isFinite(t)) headerTotal = t;
    }
    const body = await res.json();
    const items = Array.isArray(body) ? body : (body.items || body.data || body.results || []);
    if (items.length === 0) break;
    out.push(...items);
    if (items.length < PAGE_LIMIT) break;
    offset += PAGE_LIMIT;
  }
  const truncationWarning = headerTotal != null && out.length < headerTotal
    ? `User pull truncated: got ${out.length} of ${headerTotal} users — adoption computed on a partial set`
    : null;
  return { users: out, truncationWarning };
}

function round1(n) {
  return Math.round(n * 10) / 10;
}
